import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    TextField,
    Button,
    Divider,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Modal,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { changePassword, fetchUserActivityLogs, deleteAccount } from "../../store/userSlice"; // changePassword async thunk'ını ekliyoruz
import useAccountInfo from "../../hooks/useAccountInfo";
import useAuth from "../../hooks/useAuth";

const SecurityAndAccess = () => {
    const dispatch = useDispatch();
    const { logs, loading, error } = useSelector((state) => state.user); // Redux state'den aktiviteleri ve durumları alıyoruz
    const { accountInfo, loading: accountInfoLoading, error: accountInfoError } = useAccountInfo(); // Custom hook
    const [isUserApproved, setIsUserApproved] = useState(false);

    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [errors, setErrors] = useState({});

    const [open, setOpen] = useState(false);

    const handleOpen = () => setOpen(true);
    const handleClose = () => setOpen(false);

    const { isAuthenticated, logout } = useAuth();

    const handleDeleteAccount = () => {
        dispatch(deleteAccount())
            .unwrap()
            .then(() => {
                logout();
                toast.success("Hesap silme işlemi başlatıldı.");
                window.location.href = "/"; 
            })
            .catch((err) => {
                toast.error(`Hesap silme başarısız: ${err}`);
            })
            .finally(() => {
                handleClose();
            });
    };

    useEffect(() => {
        if (accountInfo) {
            setIsUserApproved(accountInfo.userApproved || false);
        }
    }, [accountInfo]);

    // Şifre validasyon kuralları
    const validatePassword = () => {
        let tempErrors = {};
        let isValid = true;

        // Mevcut şifre kontrolü
        if (!currentPassword) {
            tempErrors.currentPassword = "Mevcut şifrenizi giriniz";
            isValid = false;
        }

        // Yeni şifre kontrolü
        if (!newPassword) {
            tempErrors.newPassword = "Yeni şifrenizi giriniz";
            isValid = false;
        } else if (newPassword.length < 8) {
            tempErrors.newPassword = "Şifre en az 8 karakter olmalıdır";
            isValid = false;
        } else if (!/(?=.*[a-z])/.test(newPassword)) {
            tempErrors.newPassword = "Şifre en az bir küçük harf içermelidir";
            isValid = false;
        } else if (!/(?=.*[A-Z])/.test(newPassword)) {
            tempErrors.newPassword = "Şifre en az bir büyük harf içermelidir";
            isValid = false;
        } else if (!/(?=.*\d)/.test(newPassword)) {
            tempErrors.newPassword = "Şifre en az bir rakam içermelidir";
            isValid = false;
        } else if (!/(?=.*[@$!%*?&])/.test(newPassword)) {
            tempErrors.newPassword = "Şifre en az bir özel karakter içermelidir (@$!%*?&)";
            isValid = false;
        }

        // Şifre onayı kontrolü
        if (!confirmPassword) {
            tempErrors.confirmPassword = "Şifre onayını giriniz";
            isValid = false;
        } else if (confirmPassword !== newPassword) {
            tempErrors.confirmPassword = "Şifreler eşleşmiyor";
            isValid = false;
        }

        setErrors(tempErrors);
        return isValid;
    };

    useEffect(() => {
        dispatch(fetchUserActivityLogs())
            .unwrap()
            .catch((err) => {
                toast.error(`Loglar alınırken bir hata oluştu: ${err}`);
            });
    }, [dispatch]);

    const handlePasswordChange = () => {
        if (validatePassword()) {
            dispatch(changePassword({ password: currentPassword, newPassword }))
                .unwrap()
                .then(() => {
                    toast.success("Şifre başarıyla değiştirildi!");
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                    setErrors({});
                })
                .catch((err) => {
                    toast.error(`Şifre değiştirme başarısız: ${err}`);
                });
        }
    };

    return (
        <Box sx={{ maxWidth: 800, margin: "0 auto" }}>
            {/* Şifre Değiştir */}
            <Typography variant="h6" gutterBottom>
                Şifre Değiştir
            </Typography>
            <Box sx={{ marginBottom: 4 }}>
                <TextField
                    label="Mevcut Şifreniz"
                    type="password"
                    fullWidth
                    sx={{ marginBottom: 2 }}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    error={!!errors.currentPassword}
                    helperText={errors.currentPassword}
                />
                <TextField
                    label="Yeni Şifre"
                    type="password"
                    fullWidth
                    sx={{ marginBottom: 2 }}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    error={!!errors.newPassword}
                    helperText={errors.newPassword}
                />
                <TextField
                    label="Şifre Onayı"
                    type="password"
                    fullWidth
                    sx={{ marginBottom: 2 }}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    error={!!errors.confirmPassword}
                    helperText={errors.confirmPassword}
                />
                {error && (
                    <Alert severity="error" sx={{ marginBottom: 2 }}>
                        {error}
                    </Alert>
                )}
                <Box sx={{ marginTop: 4, textAlign: "right" }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handlePasswordChange}
                        disabled={loading}
                    >
                        {loading ? "Kaydediliyor..." : "Kaydet"}
                    </Button>
                </Box>
            </Box>

            <Divider sx={{ marginY: 4 }} />

            {/* E-Devlet Doğrulaması */}
            <Typography variant="h6" gutterBottom>
                E-Devlet ile Kimlik Doğrulaması
            </Typography>
            <Box sx={{ marginBottom: 4 }}>
                {isUserApproved ? (
                    <Alert
                        severity="success"
                        sx={{
                            width: '100%',
                            height: '56px',
                            display: 'flex',
                            alignItems: 'center',
                            backgroundColor: '#e8f5e9'  // Açık yeşil arka plan
                        }}
                    >
                        E-Devlet doğrulaması yapıldı
                    </Alert>
                ) : (
                    <Button
                        variant="contained"
                        sx={{
                            backgroundColor: "#c62828",
                            "&:hover": { backgroundColor: "#b71c1c" },
                            height: '56px',
                            width: '50%'
                        }}
                    >
                        E-Devlet ile Doğrula
                    </Button>
                )}
            </Box>

            <Divider sx={{ marginY: 4 }} />

            {/* Hesap Giriş Logları */}
            <Typography variant="h6" gutterBottom>
                Kullanıcı Hareketleri
            </Typography>
            <TableContainer component={Paper} sx={{ marginBottom: 4 }}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Kayıt Tarihi</TableCell>
                            <TableCell>IP Adresi</TableCell>
                            <TableCell>Aktivite Türü</TableCell>
                            <TableCell>Sonuç</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {logs.length > 0 ? (
                            logs.map((log) => (
                                <TableRow key={log.userActivityId}>
                                    <TableCell>
                                        {new Date(parseInt(log.registerTime)).toLocaleString()}
                                    </TableCell>
                                    <TableCell>{log.ipAddress}</TableCell>
                                    <TableCell>{log.activityType}</TableCell>
                                    <TableCell>
                                        {log.success ? "Başarılı" : "Başarısız"}
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    Kayıt bulunamadı.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Divider sx={{ marginY: 4 }} />

            {/* Hesap Silme */}
            <Typography variant="h6" gutterBottom>
                Hesap Silme
            </Typography>
            <Alert severity="warning" sx={{ marginBottom: 2 }}>

                Ardventure Kitle Fonlama hesabınızı kapattığınızda, mevcut yatırımlarınız silinmez. Hesabınızı silmeden önce gerçekleştirdiğiniz yatırımlar, MKK tarafından güvenli bir şekilde saklanacaktır. Hesabınızın tamamen silinmesi ise 15 günlük bekleme süresi sonunda gerçekleşecektir.
            </Alert>
            <Button
                variant="contained"
                fullWidth
                sx={{ backgroundColor: "#c62828", "&:hover": { backgroundColor: "#b71c1c" } }}
                onClick={handleOpen}
            >
                Hesabımı Sil
            </Button>

            <Dialog open={open} onClose={handleClose}>
                <DialogTitle>Hesap Silme</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Ardventure Kitle Fonlama hesabınızı kapattığınızda, mevcut yatırımlarınız silinmez. Hesabınızı silmeden önce gerçekleştirdiğiniz yatırımlar, MKK tarafından güvenli bir şekilde saklanacaktır. Hesabınızın tamamen silinmesi ise 15 günlük bekleme süresi sonunda gerçekleşecektir.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary">
                        Vazgeç
                    </Button>
                    <Button onClick={handleDeleteAccount} color="primary" autoFocus>
                        Onayla
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default SecurityAndAccess;
